/**
 * VideoSession — derives rich video engagement from the player's basic media
 * signals (timeupdate / pause / ended).
 *
 * Phase 1 approximations (superseded by native player events in Phase 2 — see
 * issue #104):
 *  - SEEK (rewind vs fast-forward): a timeupdate whose position jump far
 *    exceeds real elapsed wall-clock time is a seek; sign = direction.
 *  - REBUFFER: wall-clock advancing while position is stuck (and not paused).
 *
 * Emits GA4-style events via an injected `emit(name, params)` so it's pure and
 * unit-testable. `now` is injectable for deterministic tests.
 */

import { EVENTS, contentContext } from './events';

const SEEK_THRESHOLD = 2; // s discrepancy between position and wall-clock
const REBUFFER_MIN_WALL = 1.5; // s wall-clock advanced with position stuck
const REBUFFER_MAX_POS = 0.5; // s position may drift and still count as stuck
const HEARTBEAT_INTERVAL = 60; // s of playback between watch-time pings
const MILESTONES = [25, 50, 75];

const round = n => Math.round(n * 10) / 10;

export default class VideoSession {
  /**
   * @param {Object} meta - content metadata (see contentContext)
   * @param {Object} [opts]
   * @param {(name:string, params:Object)=>void} [opts.emit]
   * @param {()=>number} [opts.now] - ms clock (defaults to Date.now)
   */
  constructor(meta = {}, opts = {}) {
    this.ctx = contentContext(meta);
    this.duration = meta.duration || meta.length || 0;
    this.emit = opts.emit || (() => {});
    this.now = opts.now || (() => Date.now());
    // When a real player is bound (playerBinding.js), seek + rebuffer come from
    // native HTMLMediaElement events, so suppress the timeupdate-derived
    // heuristics here to avoid double-counting.
    this.realQoE = !!opts.realQoE;
    // Optional per-playback QoE summary provider (from playerBinding via the
    // video.js player): returns rolled-up counters { startup_ms, rebuffer_count,
    // rebuffer_seconds_total, seek_count, error_count, min_bitrate_kbps,
    // avg_bitrate_kbps, dropped_ratio } attached to the single video_complete
    // summary. Heartbeats stay lean (liveness only); QoE-over-time is carried by
    // the discrete video_rebuffer/quality_change/startup/error events.
    this.summary = typeof opts.summary === 'function' ? opts.summary : null;

    this.started = false;
    this.completeFired = false;
    this.wasPaused = false;
    this.milestonesFired = {};
    this.watched = 0; // accumulated seconds actually watched
    this.maxPosition = 0; // furthest position reached (for percent_complete)
    this.lastPos = 0;
    this.lastWall = 0;
    this.lastHeartbeat = 0;
  }

  _emit(name, params) {
    this.emit(name, { ...this.ctx, ...params });
  }

  /** Reset the play baselines (after start, seek, or resume-from-pause). */
  _rebase(position, wall) {
    this.lastPos = position;
    this.lastWall = wall;
  }

  /**
   * @param {number} position - currentTime in seconds
   * @param {number} [duration]
   */
  onTimeUpdate(position, duration) {
    const wall = this.now();
    if (duration) {
      this.duration = duration;
    }
    if (position > this.maxPosition) {
      this.maxPosition = position;
    }

    if (!this.started) {
      this.started = true;
      this.lastHeartbeat = wall;
      this._rebase(position, wall);
      this._emit(EVENTS.VIDEO_START, { position: round(position), duration: this.duration });
      return;
    }

    // Resuming after a pause: don't count the paused gap as buffering/watch.
    if (this.wasPaused) {
      this.wasPaused = false;
      this.lastHeartbeat = wall;
      this._rebase(position, wall);
      return;
    }

    const wallDelta = (wall - this.lastWall) / 1000;
    const posDelta = position - this.lastPos;

    if (this.realQoE) {
      // Native player events own seek/rebuffer — just accumulate watch time.
      // The min() guard keeps a seek jump from inflating watched seconds.
      this.watched += Math.max(0, Math.min(posDelta, wallDelta + 0.5));
      this._rebase(position, wall);
    } else if (
      Math.abs(posDelta) > SEEK_THRESHOLD &&
      Math.abs(posDelta - wallDelta) > SEEK_THRESHOLD
    ) {
      // SEEK — position moved far more than time elapsed.
      this._emit(EVENTS.VIDEO_SEEK, {
        seek_direction: posDelta < 0 ? 'rewind' : 'forward',
        seek_from: round(this.lastPos),
        seek_to: round(position),
        seek_seconds: round(Math.abs(posDelta)),
      });
      this._rebase(position, wall);
    } else if (wallDelta > REBUFFER_MIN_WALL && posDelta < REBUFFER_MAX_POS && posDelta > -0.1) {
      // REBUFFER — wall advanced but playback position stalled.
      this._emit(EVENTS.VIDEO_REBUFFER, {
        rebuffer_seconds: round(wallDelta),
        position: round(position),
      });
      this._rebase(position, wall);
    } else {
      // Normal playback — accumulate watch time (guard against clock skew).
      this.watched += Math.max(0, Math.min(posDelta, wallDelta + 0.5));
      this._rebase(position, wall);
    }

    this._checkMilestones(position);
    this._checkHeartbeat(wall, position);
  }

  _checkMilestones(position) {
    if (!this.duration) {
      return;
    }
    const percent = (position / this.duration) * 100;
    MILESTONES.forEach(m => {
      if (percent >= m && !this.milestonesFired[m]) {
        this.milestonesFired[m] = true;
        this._emit(EVENTS.VIDEO_PROGRESS, { percent: m, position: round(position) });
      }
    });
  }

  // Lean liveness ping: watch-time + position only. Kept small on purpose — it's
  // the concurrency / retention / crash-resilience signal, sent per session; the
  // rich QoE lives on the discrete events and the video_complete summary.
  _checkHeartbeat(wall, position) {
    if ((wall - this.lastHeartbeat) / 1000 >= HEARTBEAT_INTERVAL) {
      this.lastHeartbeat = wall;
      this._emit(EVENTS.VIDEO_HEARTBEAT, {
        watch_time_seconds: round(this.watched),
        position: round(position),
      });
    }
  }

  /** @param {number} position */
  onPause(position) {
    this.wasPaused = true;
    this._emit(EVENTS.VIDEO_PAUSE, { position: round(position || 0) });
  }

  /**
   * Emit the single per-playback summary (video_complete). Called on natural
   * `ended` and on tab-close flush (playerBinding) — idempotent via
   * `completeFired`, and a no-op if playback never started, so a closed tab on
   * an unplayed video doesn't fabricate a completion.
   * @param {number} position
   */
  onEnded(position) {
    if (this.completeFired || !this.started) {
      return;
    }
    this.completeFired = true;
    const pos = round(position || this.maxPosition || this.duration || 0);
    const maxPos = round(Math.max(this.maxPosition, position || 0));
    this._emit(EVENTS.VIDEO_COMPLETE, {
      position: pos,
      max_position: maxPos,
      watch_time_seconds: round(this.watched),
      ...(this.duration ? { percent_complete: round((maxPos / this.duration) * 100) } : {}),
      ...(this.summary ? this.summary() : {}),
    });
  }

  /** @param {string|Error} error */
  onError(error) {
    this._emit(EVENTS.VIDEO_ERROR, {
      message: (error && error.message) || String(error || 'unknown'),
    });
  }
}
